import { useState, useEffect } from "react";
import { Search, Users, Calendar, TrendingUp, AlertCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { leaveAPI } from "@/lib/api";

export default function EmployeeLeaveCredits({ employees = [], currentUserRole }) {
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [employeeCredits, setEmployeeCredits] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedEmployeeData, setSelectedEmployeeData] = useState(null);

  // Filter employees based on search term
  const filteredEmployees = employees.filter(employee =>
    employee.prenom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    employee.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    employee.numero_employe?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Load employee leave balance
  const loadEmployeeBalance = async (employeeId) => {
    if (!employeeId) return;

    setLoading(true);
    try {
      const response = await leaveAPI.getBalance(employeeId);
      const balanceData = response.data.data;

      setSelectedEmployeeData({
        id: employeeId,
        name: `${employees.find(e => e.id === employeeId)?.prenom} ${employees.find(e => e.id === employeeId)?.nom}`,
        numero_employe: employees.find(e => e.id === employeeId)?.numero_employe,
        ...balanceData
      });

      toast({
        title: "Succès",
        description: "Solde de congé chargé avec succès",
      });
    } catch (error) {
      console.error("Error loading employee balance:", error);
      toast({
        title: "Erreur",
        description: "Impossible de charger le solde de congé",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle employee selection
  const handleEmployeeSelect = (employeeId) => {
    setSelectedEmployee(employeeId);
    loadEmployeeBalance(employeeId);
  };

  // Calculate total accrued days (if using the new system)
  const calculateAccruedDays = (hireDate) => {
    if (!hireDate) return 0;
    const now = new Date();
    const hire = new Date(hireDate);
    const monthsDiff = (now.getFullYear() - hire.getFullYear()) * 12 + (now.getMonth() - hire.getMonth());
    return monthsDiff * 1.5;
  };

  // Only show for managers, HR, and admin
  if (!currentUserRole || !['manager', 'rh', 'admin'].includes(currentUserRole)) {
    return null;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Recherche des crédits de congé
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search and Select */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher un employé..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            <Select value={selectedEmployee} onValueChange={handleEmployeeSelect}>
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner un employé" />
              </SelectTrigger>
              <SelectContent>
                {filteredEmployees.map((employee) => (
                  <SelectItem key={employee.id} value={employee.id}>
                    {employee.prenom} {employee.nom} ({employee.numero_employe})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Employee Balance Display */}
          {selectedEmployeeData && (
            <Card className="mt-6 bg-blue-50 border-blue-200">
              <CardHeader>
                <CardTitle className="text-lg">
                  Solde de congé - {selectedEmployeeData.name}
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  Numéro employé: {selectedEmployeeData.numero_employe}
                </p>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    <span className="ml-2">Chargement...</span>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Accrued Days */}
                    <div className="text-center">
                      <div className="text-3xl font-bold text-blue-600 mb-2">
                        {selectedEmployeeData.accruedDays?.toFixed(1) || '0.0'}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Jours acquis
                      </div>
                      <div className="text-xs text-blue-600 mt-1">
                        (1.5 jours/mois)
                      </div>
                    </div>

                    {/* Used Days */}
                    <div className="text-center">
                      <div className="text-3xl font-bold text-orange-600 mb-2">
                        {selectedEmployeeData.usedDays?.toFixed(1) || '0.0'}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Jours consommés
                      </div>
                      <div className="text-xs text-orange-600 mt-1">
                        (cette année)
                      </div>
                    </div>

                    {/* Remaining Days */}
                    <div className="text-center">
                      <div className={`text-3xl font-bold mb-2 ${(selectedEmployeeData.remainingDays || 0) < 5
                        ? 'text-red-600'
                        : (selectedEmployeeData.remainingDays || 0) < 10
                          ? 'text-orange-600'
                          : 'text-green-600'
                        }`}>
                        {selectedEmployeeData.remainingDays?.toFixed(1) || '0.0'}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Solde restant
                      </div>
                      <div className={`text-xs mt-1 ${(selectedEmployeeData.remainingDays || 0) < 5
                        ? 'text-red-600'
                        : (selectedEmployeeData.remainingDays || 0) < 10
                          ? 'text-orange-600'
                          : 'text-green-600'
                        }`}>
                        {selectedEmployeeData.remainingDays < 5
                          ? 'Solde faible'
                          : selectedEmployeeData.remainingDays < 10
                            ? 'Solde moyen'
                            : 'Solde suffisant'
                        }
                      </div>
                    </div>
                  </div>
                )}

                {/* Additional Info */}
                {!loading && selectedEmployeeData && (
                  <div className="mt-6 p-4 bg-white rounded-lg border">
                    <h4 className="font-medium mb-3">Informations détaillées</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Taux d'accumulation:</span>
                        <span className="ml-2 font-medium">1.5 jours par mois</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Période:</span>
                        <span className="ml-2 font-medium">Année civile (janvier-décembre)</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Calcul automatique:</span>
                        <span className="ml-2 font-medium">Oui</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Dernière mise à jour:</span>
                        <span className="ml-2 font-medium">
                          {new Date().toLocaleDateString('fr-FR')}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Quick Employee List */}
          <div className="mt-6">
            <h4 className="font-medium mb-3">Tous les employés</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[500px] overflow-y-auto pr-2">
              {filteredEmployees.map((employee) => (
                <Card
                  key={employee.id}
                  className="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => handleEmployeeSelect(employee.id)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">
                          {employee.prenom} {employee.nom}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {employee.numero_employe}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEmployeeSelect(employee.id);
                        }}
                      >
                        Voir solde
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}